import type { FormEvent } from "react";
import styles from "./RegisterToolbar.module.css";
import type {
	KindFilter,
	RegisterFilters,
	SortKey,
	StatusFilter,
} from "./lib/registerFilters";

export interface RegisterToolbarProps {
	searchInput: string;
	onSearchInput: (value: string) => void;
	/** Applies the typed search; the toolbar never filters as you type. */
	onSearchSubmit: () => void;
	onSearchClear: () => void;
	filters: RegisterFilters;
	onKindChange: (kind: KindFilter) => void;
	onStatusChange: (status: StatusFilter) => void;
	sort: SortKey;
	onSortChange: (sort: SortKey) => void;
	grouped: boolean;
	onGroupedChange: (grouped: boolean) => void;
	/** True while the one-click "Needs payment" view is exactly what is set. */
	needsPayment: boolean;
	onNeedsPayment: () => void;
	shownCount: number;
	totalCount: number;
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
	{ value: "all", label: "All statuses" },
	{ value: "outstanding", label: "Outstanding (sent + part paid)" },
	{ value: "draft", label: "Draft" },
	{ value: "sent", label: "Sent" },
	{ value: "part-paid", label: "Part paid" },
	{ value: "paid", label: "Paid" },
	{ value: "accepted", label: "Accepted (quotes)" },
	{ value: "expired", label: "Expired (quotes)" },
];

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
	{ value: "newest", label: "Newest first" },
	{ value: "oldest", label: "Oldest first" },
	{ value: "balance", label: "Balance, highest" },
	{ value: "total", label: "Total, highest" },
	{ value: "client", label: "Client A–Z" },
	{ value: "number", label: "Number" },
];

/**
 * The register's controls. Search is deferred to submit rather than filtering
 * per keystroke: beyond matching the rest of the app, live filtering would
 * collapse an open row every time the user typed a character.
 *
 * Grouping and sorting are mutually exclusive by nature — a job group has no
 * balance or total to be ordered by — so the chip is disabled, with the reason
 * stated, rather than silently ignored under a sort.
 */
export function RegisterToolbar({
	searchInput,
	onSearchInput,
	onSearchSubmit,
	onSearchClear,
	filters,
	onKindChange,
	onStatusChange,
	sort,
	onSortChange,
	grouped,
	onGroupedChange,
	needsPayment,
	onNeedsPayment,
	shownCount,
	totalCount,
}: RegisterToolbarProps) {
	const groupingAvailable = sort === "newest";

	const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		onSearchSubmit();
	};

	return (
		<form className={styles.root} role="search" onSubmit={handleSubmit}>
			<input
				className={styles.search}
				type="search"
				name="documentSearch"
				aria-label="Search by number, client or property"
				placeholder="Search number, client or property…"
				value={searchInput}
				onChange={(event) => onSearchInput(event.currentTarget.value)}
				onKeyDown={(event) => {
					if (event.key === "Escape") onSearchClear();
				}}
			/>
			<button type="submit" className={styles.searchButton}>
				Search
			</button>

			<select
				className={styles.select}
				aria-label="Filter by document type"
				value={filters.kind}
				onChange={(event) => onKindChange(event.currentTarget.value as KindFilter)}
			>
				<option value="all">All documents</option>
				<option value="invoice">Invoices</option>
				<option value="quote">Quotes</option>
			</select>

			<select
				className={styles.select}
				aria-label="Filter by status"
				value={filters.status}
				onChange={(event) => onStatusChange(event.currentTarget.value as StatusFilter)}
			>
				{STATUS_OPTIONS.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>

			<select
				className={styles.select}
				aria-label="Sort documents"
				value={sort}
				onChange={(event) => onSortChange(event.currentTarget.value as SortKey)}
			>
				{SORT_OPTIONS.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>

			<button
				type="button"
				className={`${styles.chip}${needsPayment ? " is-active" : ""}`}
				aria-pressed={needsPayment}
				onClick={onNeedsPayment}
			>
				Needs payment
			</button>

			<button
				type="button"
				className={`${styles.chip}${grouped && groupingAvailable ? " is-active" : ""}`}
				aria-pressed={grouped && groupingAvailable}
				disabled={!groupingAvailable}
				title={
					groupingAvailable ? undefined : "Grouping is off while the table is sorted."
				}
				onClick={() => onGroupedChange(!grouped)}
			>
				Group by job
			</button>

			<span className={styles.push} />
			<p className={styles.count} role="status">
				Showing {shownCount} of {totalCount}
			</p>
		</form>
	);
}
